import { logger } from '@adornis/base/logging.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getLLMRouter } from './llm/llm-gate.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { calcMaxPointsModel, calcMaxPointsTemperature } from '../../shared/models.js';
import { addCostToExam } from './add-cost.js';

export const calculateMaxPointsFromCriteriaMutation = registerMutation({
  operationName: 'calculateMaxPointsFromCriteriaMutation',
  type: () => Float,
  params: [
    { name: 'criteria', type: () => String },
    { name: 'masterSolution', type: () => String },
    { name: 'examId', type: () => String },
  ],
  resolve: (criteria: string, masterSolution: string, examId?: string) => async () => {
    const router = getLLMRouter({}, 'interactive');

    const llmCall = router.chat.completions.create({
      model: calcMaxPointsModel,
      provider: 'openrouter',
      temperature: calcMaxPointsTemperature,
      messages: [
        {
          role: 'system',
          content: `Du bist ein Assistent, der die maximale Gesamtpunktzahl einer Aufgabe aus einem Bewertungsschema berechnet.
Analysiere das Bewertungsschema und die Musterlösung sorgfältig.
Antworte NUR mit einer einzigen Zahl (z.B. "3" oder "2.5"). Keine Erklärung, keine Einheit, nur die Zahl.`,
        },
        {
          role: 'user',
          content: `Bewertungsschema:\n${criteria}\n\nMusterlösung:\n${masterSolution}\n\nWie viele Punkte können maximal erreicht werden?`,
        },
      ],
    });

    llmCall.catch(err => {
      logger.warn({ err }, 'Background llmCall rejected after timeout race concluded.');
    });

    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new ExpectedError('calculateMaxPoints timeout'));
      }, 8_000);
    });

    try {
      const response = await Promise.race([llmCall, timeout]);
      await addCostToExam(response, examId, 'calcMaxPoints');
      const raw = response.choices[0]?.message.content ?? '';
      const parsed = parseFloat(raw.replace(',', '.').replace(/[^\d.]/g, ''));
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    } catch (error) {
      logger.warn(
        { err: error },
        'calculateMaxPointsFromCriteriaMutation failed or timed out. Falling back to 0 points.',
      );
      return 0; // Return a safe numerical placeholder to keep the processing chain active
    }
  },
});
